import { useCallback, useEffect, useState } from "react";
import _, { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { Tabs, Tab, ScrollShadow } from "@nextui-org/react";
import { requestAuthPost, requestAuthGet } from "../../../api/authInstance";
import { checkLoyalty } from "../../../api/loyaltyInstance";
import { useAuth } from "../../auth/authContext";
import { sheetNotify, toastNotify } from "../../../utils/Utils";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../../hooks/useWindowSize";

import { ReactComponent as QRIcon } from "../../../assets/icons/qr-code-icon.svg";
import { ReactComponent as BarcodeIcon } from "../../../assets/icons/barcode-icon.svg";
import { ReactComponent as LoyaltyIcon } from "../../../assets/icons/user-circle.svg";
import SearchIcon from "../../../assets/icons/search-md.svg";
import ModalLoading from "../../ModalLoading";
import QRScanner from "../../../fragment/QRScanner";
import InformationSheet from "../../Sheet/InformationSheet";
import Button from "../../Button/Button";
import InputIcon from "../../Input/InputIcon";
import ModalBottom from "../ModalBottom";
import FormInput from "../../Input/FormInput";
import ModalBasic from "../../ModalBasic";
import BarcodeScanner from "../../Scanner/BarcodeScanner";
import ModalChild from "./ModalChild";
import Skeleton from "./Skeleton";

export default function ModalRedeem({ setMenu, isOpen, setIsOpen }) {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const [qrResult, setQrResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [projectData, setProjectData] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [skuData, setSkuData] = useState([]);
  const [selectedMechanism, setSelectedMechanism] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [mechanismData, setMechanismData] = useState([]);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState("scan");
  const [selectedScanner, setSelectedScanner] = useState("qr");
  const [isScanned, setIsScanned] = useState(false);
  const [failedScan, setFailedScan] = useState("");
  const [results, setResults] = useState([]);
  const [showModalLoading, setShowModalLoading] = useState(false);

  // sheet state
  const [sheetDetail, setSheetDetail] = useState({
    title: "",
    detail: "",
    open: false,
  });

  useEffect(() => {
    if (selected === "manual") {
      getData("");
    }
  }, [selected]);

  async function getData(search) {
    // show modal loading until data loaded
    setIsLoading(true);
    try {
      // get list voucher active
      let res = await requestAuthGet(`option/project`, {
        params: {
          type: "42f7e8fe-b5de-4843-8506-707e8dc37e7c",
          isActive: true,
          limit: 9999,
          retailer: userData?.outlet?.retailer?.id,
          outlet: userData?.outlet?.id,
          keyword: search,
        },
      });
      setProjectData(res.data.data);
    } catch (error) {
      console.error(error);
    }
    // hide modal loading after data is loaded
    setIsLoading(false);
  }

  async function getMechanism(project) {
    // show modal loading until data loaded
    setSelectedProject(project);
    setStep(2);
    setIsLoading(true);
    try {
      // get list voucher active
      let res = await requestAuthGet(`voucher/retailer`, {
        params: {
          project: project.id,
          retailer: userData?.outlet?.retailer?.id,
          outlet: userData?.outlet?.id,
        },
      });
      setMechanismData(res.data.data);
    } catch (error) {
      console.error(error);
    }
    // hide modal loading after data is loaded
    setIsLoading(false);
  }

  const debounced = useCallback(debounce(getData, 500), []);

  const onDetected = (result) => {
    setResults(results.concat([result]));
    setIsScanned(true);
  };

  const navigate = useNavigate();

  const scannerTabs = [
    {
      id: "qr",
      label: "Voucher",
      icon: <QRIcon className="size-5" />,
      content: (
        <div className="px-0 md:px-[calc(11rem-5vw)]">
          <QRScanner
            key="voucher-qr"
            setQrResult={setQrResult}
            setIsScanned={setIsScanned}
            ignoredResult={selectedScanner === "qr" ? failedScan : ""}
            isOpen={selectedScanner === "qr" && isOpen && selected === "scan"}
          />
        </div>
      ),
    },
    {
      id: "loyalty",
      label: "Loyalty",
      icon: <LoyaltyIcon className="size-5" />,
      content: (
        <div className="px-0 md:px-[calc(11rem-5vw)]">
          <QRScanner
            key="loyalty-qr"
            setQrResult={setQrResult}
            setIsScanned={setIsScanned}
            ignoredResult={selectedScanner === "loyalty" ? failedScan : ""}
            isOpen={
              selectedScanner === "loyalty" && isOpen && selected === "scan"
            }
          />
        </div>
      ),
    },
    // {
    //   id: "barcode",
    //   label: "Barcode",
    //   icon: <BarcodeIcon />,
    //   content: (
    //     <BarcodeScanner
    //       onDetected={onDetected}
    //       isOpen={selectedScanner === "barcode" && isOpen}
    //     />
    //   ),
    // },
  ];

  const tabs = [
    {
      id: "scan",
      label: "Scan",
      content: (
        <div className=" mb-5 space-y-5 bg-white">
          <Tabs
            fullWidth
            aria-label="Tabs form"
            items={scannerTabs}
            disabledKeys={["barcode"]}
            placement="bottom"
            size="sm"
            selectedKey={selectedScanner}
            onSelectionChange={(key) => {
              setSelectedScanner(key);
              setQrResult("");
              setResults([]);
              setIsScanned(false);
              setFailedScan("");
              setErrorMsg("");
            }}
            classNames={{
              tabList: "bg-transparent-black-5 rounded-xl p-1.5 mx-3",
              cursor: "!bg-monochrome-0 rounded-lg",
              tab: "py-5 text-monochrome-600 font-semibold",
            }}
          >
            {(item) => (
              <Tab
                key={item.id}
                title={
                  <div className="flex items-center space-x-2">
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                }
                className="px-0 focus:outline-none"
              >
                <div className="py-2">{item.content}</div>
              </Tab>
            )}
          </Tabs>
        </div>
      ),
    },
    {
      id: "manual",
      label: "Manual",
      content: (
        <div>
          <InputIcon
            icon={SearchIcon}
            placeholder="Search Project"
            onChange={(e) => {
              setSearch(e.target.value);
              debounced(e.target.value, 1000);
            }}
          />
          <div className="flex flex-col gap-3 py-4">
            <p className="mb-1 text-xs text-monochrome-300">
              {t("redeem.activeProjectList")}
            </p>
            <ScrollShadow className="h-[400px] space-y-3">
              {!isLoading ? (
                projectData.map((project, i) => (
                  <button
                    key={i}
                    className="flex w-full items-center gap-4 text-start"
                    onClick={() => getMechanism(project)}
                  >
                    {project.img ? (
                      <img
                        src={project.img}
                        alt="V"
                        className="h-10 min-h-10 w-10 min-w-10 rounded-lg border border-monochrome-50 object-cover"
                      />
                    ) : (
                      <div className="flex h-10 min-h-10 w-10 min-w-10 items-center justify-center rounded-lg bg-monochrome-50 font-semibold text-white">
                        {project.alias ? project.alias[0] : project.name[0]}
                      </div>
                    )}
                    <div className="col-span-4 flex h-full flex-col">
                      <p className="font-semibold">
                        {project.alias ?? project.name}
                      </p>
                      <p className="text-[10px] text-monochrome-200">
                        {project.name}
                      </p>
                    </div>
                  </button>
                ))
              ) : (
                <>
                  <Skeleton />
                </>
              )}
            </ScrollShadow>
          </div>
        </div>
      ),
    },
  ];

  async function CheckVoucher() {
    const scanResult =
      selectedScanner === "barcode"
        ? _.last(results)?.codeResult?.code
        : qrResult;

    if (
      selectedScanner === "loyalty" &&
      scanResult &&
      scanResult === failedScan
    ) {
      return;
    }

    setShowModalLoading(true);

    try {
      if (selectedScanner === "loyalty") {
        const loyaltyDetail = await checkLoyalty(scanResult);

        setIsOpen(false);
        navigate("/confirm-loyalty", {
          state: { loyaltyDetail },
        });
        return;
      }

      const res = await requestAuthPost("uvc/check", {
        uvc: scanResult,
        retailer: userData?.outlet?.retailer?.id,
      });

      if (res.error) {
        setIsScanned(false);
        setIsOpen(false);
        sheetNotify(
          {
            title: "Oops..",
            detail: res.detailMessage,
            buttonLabel: "Coba Lagi",
            buttonAction: () => {
              setIsOpen(true);
              setSheetDetail({ ...sheetDetail, open: false });
            },
          },
          setSheetDetail,
        );
        setErrorMsg(res.detailMessage);
      } else {
        if (!_.isEmpty(res)) {
          const detailVoucher = res.data.data;

          const settingsData = await requestAuthGet(`setting`, {
            params: {
              outlet: userData.outlet?.id,
              project:
                detailVoucher?.uvc?.voucher?.project?.id ??
                detailVoucher?.project?.id,
              voucher: detailVoucher.id,
            },
          });

          if (settingsData.error) {
            setErrorMsg("Terjadi kesalahan saat memuat data settings.");
          } else {
            navigate("/confirm-voucher", {
              state: {
                detailVoucher,
                settings: settingsData.data.data,
                redemptionMethod: "SCAN",
              },
            });
          }
        }
      }
    } catch (error) {
      console.error("Terjadi kesalahan:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Terjadi kesalahan saat memproses permintaan.";

      setIsScanned(false);
      setErrorMsg(message);

      if (selectedScanner === "loyalty") {
        setFailedScan(scanResult);
        toastNotify(message, "error");
      }
    } finally {
      setShowModalLoading(false);
    }
  }

  async function getSettings(project, voucher, dynamicSku) {
    if (voucher.uvcOpen < 1)
      return toastNotify(
        "Maaf, alokasi voucher untuk mekanisme ini tidak tersedia",
        "error",
      );

    // show modal loading until data loaded
    setShowModalLoading(true);
    try {
      const settingsResponse = await requestAuthGet(`setting`, {
        params: {
          outlet: userData?.outlet?.id,
          project: project,
          voucher: voucher.id,
        },
      });
      const settingsData = settingsResponse.data.data;
      const data = settingsData;

      if (data.dompet?.redemption?.method === "MANUAL") {
        // sum uvc quantity
        const uvcQty = skuData.reduce((acc, item) => acc + item.quantity, 0);

        // retrieve uvc when manual redemption
        const retrieveResponse = await requestAuthPost("uvc/retrieve", {
          voucher: voucher.id,
          retailer: userData?.outlet?.retailer?.id,
          countActivation: dynamicSku ? dynamicSku[0].quantity : uvcQty,
        });

        if (!retrieveResponse.error) {
          const { data } = retrieveResponse.data || {};
          navigate("/confirm-voucher", {
            state: {
              detailVoucher: voucher,
              manualUvc: data ?? [],
              redemptionMethod: "MANUAL",
              multipleSkuId: dynamicSku ? dynamicSku : skuData,
              settings: settingsData,
            },
          });
        } else {
          toastNotify(retrieveResponse.detailMessage, "error");
        }
      } else {
        toastNotify(t("redeem.mechanismError"), "error");
      }
    } catch (error) {
      console.error(error);
    } finally {
      // hide modal loading after the whole flow completes
      setShowModalLoading(false);
    }
  }

  /**
   * This useEffect will be triggered when the QR code is scanned
   * and the `isScanned` state is true. It will call the `CheckVoucher`
   * function to check the voucher via API.
   */
  useEffect(() => {
    if (isScanned) {
      CheckVoucher();
    }
    // eslint-disable-next-line
  }, [isScanned]);

  return (
    <>
      <ModalLoading
        showModal={showModalLoading}
        setShowModal={setShowModalLoading}
      />
      {width < 768 ? (
        <ModalBottom
          showModal={isOpen}
          setShowModal={setIsOpen}
          defaultHeight={0.9}
          footer={
            <>
              {selected === "scan" && (
                <div className="flex w-full flex-col gap-2">
                  <div>
                    <FormInput
                      placeholder={t("redeem.title")}
                      value={qrResult}
                      isInvalid={errorMsg.length > 0}
                      onFocus={() => setErrorMsg("")}
                      onChange={(e) => setQrResult(e.target.value)}
                      type="text"
                    />
                    {errorMsg.length > 0 && (
                      <div className="mt-1 text-2xs text-danger">
                        {errorMsg}
                      </div>
                    )}
                  </div>
                  <Button
                    label={t("redeem.confirmation")}
                    onClick={CheckVoucher}
                    disabled={_.isEmpty(qrResult)}
                    fullWidth
                    variant="gradient"
                  />
                </div>
              )}
            </>
          }
          header={
            <>
              {step > 1 && (
                <button
                  className="absolute left-5 top-6"
                  onClick={() => setStep(step - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
              <div
                className={`${step > 1 ? "px-14" : "px-0"} text-lg font-semibold`}
              >
                {step === 2 && selectedProject
                  ? selectedProject.alias ?? selectedProject.name
                  : step === 3
                    ? selectedMechanism.voucher?.mechanism ?? "Pilih SKU"
                    : "Redeem"}
              </div>
            </>
          }
        >
          <ModalChild
            step={step}
            setStep={setStep}
            tabs={tabs}
            selected={selected}
            setSelected={setSelected}
            showModalLoading={showModalLoading}
            isLoading={isLoading}
            mechanismData={mechanismData}
            getSettings={getSettings}
            selectedProject={selectedProject}
            selectedMechanism={selectedMechanism}
            setSelectedMechanism={setSelectedMechanism}
            setSkuData={setSkuData}
            skuData={skuData}
          />
        </ModalBottom>
      ) : (
        <ModalBasic
          name="tnc"
          open={isOpen}
          setOpen={setIsOpen}
          title="Redeem"
          enClose={true}
          footer={
            <>
              {selected === "scan" ? (
                <div className="flex w-full flex-col gap-2">
                  <div>
                    <FormInput
                      placeholder={t("redeem.title")}
                      value={
                        selectedScanner === "barcode"
                          ? _.last(results)?.codeResult?.code
                          : qrResult
                      }
                      isInvalid={errorMsg.length > 0}
                      onFocus={() => setErrorMsg("")}
                      onChange={(e) => setQrResult(e.target.value)}
                      type="text"
                    />
                    {errorMsg.length > 0 && (
                      <div className="mt-1 text-2xs text-danger">
                        {errorMsg}
                      </div>
                    )}
                  </div>
                  <Button
                    label={t("redeem.confirmation")}
                    onClick={CheckVoucher}
                    disabled={_.isEmpty(qrResult)}
                    fullWidth
                    variant="gradient"
                  />
                </div>
              ) : (
                step === 3 &&
                selectedMechanism?.voucher &&
                selectedMechanism?.voucher?.voucherMethod !== "DYNAMIC" && (
                  <Button
                    label="Konfirmasi Tukar Voucher"
                    variant="gradient"
                    disabled={_.isEmpty(skuData)}
                    onClick={() => {
                      getSettings(
                        selectedProject.id,
                        selectedMechanism.voucher,
                      );
                    }}
                    fullWidth
                  />
                )
              )}
            </>
          }
          header={
            <>
              {step > 1 && (
                <button
                  className="sticky left-5 top-6"
                  onClick={() => setStep(step - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
              <div
                className={`${step > 1 ? "px-2" : "px-0"} text-lg font-semibold`}
              >
                {step === 2 && selectedProject
                  ? selectedProject.alias ?? selectedProject.name
                  : step === 3
                    ? "Pilih SKU"
                    : "Redeem"}
              </div>
            </>
          }
        >
          <ModalChild
            step={step}
            setStep={setStep}
            tabs={tabs}
            selected={selected}
            setSelected={setSelected}
            isLoading={isLoading}
            showModalLoading={showModalLoading}
            mechanismData={mechanismData}
            getSettings={getSettings}
            selectedProject={selectedProject}
            selectedMechanism={selectedMechanism}
            setSelectedMechanism={setSelectedMechanism}
            setSkuData={setSkuData}
            skuData={skuData}
          />
        </ModalBasic>
      )}

      {/* Information Sheet */}
      <InformationSheet
        sheetDetail={sheetDetail}
        setSheetDetail={setSheetDetail}
      />
    </>
  );
}
